import { TableColumnSticky, TableHeaderVariant } from '../../../types/DesignSystem/Table';

/**
 * The class recipe for a pinned (sticky) column, in one place because it has to be
 * applied identically in four: the header `<th>`, the body `<td>`, the first-load
 * skeleton `<td>`, and any cell a caller renders by hand.
 *
 * `position: sticky` resolves against the nearest scrolling ancestor, which for
 * `Table` is the wrapper `<div>` carrying `tw-overflow-x-auto` — so a pinned cell
 * holds its place against horizontal scroll without any JS.
 *
 * **Offset is always 0** — one pinned column per edge. A second pinned column needs
 * `left: <width of the first>`, and that can only be summed from absolute pixel
 * widths; every table in this app declares percentages (`width: '25%'` throughout
 * `ISRProjectTable`). An offset computed from a percentage would be silently wrong,
 * which is worse than not offering it. "First and last column" — the actual
 * requirement, and what the legacy markup pinned — needs nothing more.
 *
 * The divider (`border-r` on a left pin, `border-l` on a right pin) is what separates
 * the pinned column from the cells sliding beneath it; without it the two read as one
 * continuous row. Cell borders paint normally under `border-collapse: collapse` — it
 * is the *table's* own border that does not (§ 38).
 */
/**
 * The stacking ladder. One entry per layer, so a change is a change to the ladder
 * rather than to a string buried in a template literal.
 */
export const STICKY_Z = {
  /** A body cell pinned to an edge: above the ordinary cells sliding beneath it. */
  bodyPinned: 'tw-z-10',
  /** A header cell pinned only to the top: above the body rows sliding beneath it. */
  headerTop: 'tw-z-20',
  /** A header cell pinned to an edge — the corner, which must beat both of the above. */
  headerEdge: 'tw-z-30',
} as const;

export const stickyBodyCellClasses = (
  sticky: TableColumnSticky | undefined,
): string => {
  if (!sticky) return '';
  const edge = sticky === 'left' ? 'tw-left-0 tw-border-r' : 'tw-right-0 tw-border-l';
  return `tw-sticky ${edge} ${STICKY_Z.bodyPinned} tw-border-edge`;
};

/**
 * How far the table is scrolled past its pins — measured by `Table` from the one
 * scrolling wrapper, threaded to every pinned cell. Both false in jsdom (no
 * layout) and at rest, which is the correct default: the seam earns its shadow
 * only while something is actually sliding beneath it.
 */
export interface StickyEdgeState {
  /** `scrollLeft > 0` — columns have slid under a LEFT pin. */
  left: boolean;
  /** More columns remain beyond the right edge — they slide under a RIGHT pin. */
  right: boolean;
}

/**
 * The § 222 layer cue: while the table is scrolled past a pin, the pinned cell
 * casts `sm` sideways onto the cells sliding beneath it — the hairline divider
 * says "boundary", the shadow says "this is ON TOP of that", and only the second
 * is true once the columns start moving. One class, decided here, so a corner
 * cell can never stack two shadow utilities (stylesheet order would pick the
 * winner invisibly — the TableCell ink rule).
 */
export const stickyEdgeShadow = (
  sticky: TableColumnSticky | undefined,
  edge: StickyEdgeState,
): string => {
  if (sticky === 'left' && edge.left) return 'tw-shadow-ds-sm-right';
  if (sticky === 'right' && edge.right) return 'tw-shadow-ds-sm-left';
  return '';
};

/**
 * The header cell's ONE shadow (§ 222). Two cues can apply to a corner cell —
 * rows scrolled under the pinned top, columns scrolled past the pinned edge —
 * and two `tw-shadow-*` utilities on one element leave the winner to stylesheet
 * order, so the choice is made here: the sideways cue wins on the corner (it is
 * the rarer, more disorienting scroll), and the top-pinned row wears `sm` once
 * anything is under it. All-false input (rest, or jsdom) is no shadow — the
 * scroll-aware contract everywhere in this system.
 */
export const stickyHeaderShadow = (
  sticky: TableColumnSticky | undefined,
  stickyHeader: boolean,
  scrolled: { under: boolean } & StickyEdgeState,
): string => {
  const sideways = stickyEdgeShadow(sticky, scrolled);
  if (sideways) return sideways;
  if (stickyHeader && scrolled.under) return 'tw-shadow-ds-sm';
  return '';
};

/**
 * The class recipe for a HEADER cell, which can be pinned two ways at once.
 *
 * **Both pins are resolved here, in one place, because they share a z-index and only
 * one may be emitted.** Composing two helpers gave a doubly-pinned corner cell two
 * `tw-z-*` classes, and which one won was then decided by stylesheet order rather than
 * by intent — the same trap `TableCell` records for its ink classes.
 *
 * **The ladder, and the bug that produced it.** With `stickyHeader` on, EVERY header
 * cell is pinned, so a corner cell (pinned to an edge *and* the top) tied with its
 * ordinary neighbours at `z-20` — and a tie is broken by DOM order, so the next column
 * along, painting later, covered the corner. Measured in headless Chromium on the real
 * emitted CSS: with the table scrolled 400px right, `elementFromPoint` at the centre of
 * the pinned "Rule Name" corner returned the **Category 1** header. The label was in the
 * DOM, correctly positioned at `left: 1px`, and invisible.
 *
 * So an edge pin outranks a top-only pin:
 *
 * | layer | z | why |
 * |---|---|---|
 * | ordinary body cell | auto | nothing to clear |
 * | body cell pinned to an edge | 10 | covers the cells scrolling under it |
 * | header cell pinned to the top | 20 | covers the rows scrolling under it |
 * | header cell pinned to an EDGE | 30 | the corner — must beat both of the above |
 */
export const stickyHeaderCellClasses = (
  sticky: TableColumnSticky | undefined,
  stickyHeader: boolean,
): string => {
  if (!sticky && !stickyHeader) return '';

  const parts = ['tw-sticky'];
  if (sticky === 'left') parts.push('tw-left-0', 'tw-border-r', 'tw-border-edge');
  if (sticky === 'right') parts.push('tw-right-0', 'tw-border-l', 'tw-border-edge');
  if (stickyHeader) parts.push('tw-top-0');
  parts.push(sticky ? STICKY_Z.headerEdge : STICKY_Z.headerTop);

  return parts.join(' ');
};

/**
 * The ground a pinned cell paints for itself.
 *
 * A `<tr>` background is enough for a static cell, but a pinned cell is composited
 * over its scrolled-away neighbours and the row's background does not travel with it.
 * Left transparent, the pinned column becomes a window onto whatever slid underneath:
 * two columns of text overprinted. So the row's *resolved* ground — striped or not —
 * is handed down to the pinned cells only.
 *
 * `group-hover:` rather than `hover:` is the other half of the same problem. Once the
 * cell is opaque, the `<tr>`'s own `hover:tw-bg-surface-sunken` no longer shows
 * through it, so a hoverable table would tint every column except the pinned ones. A
 * `hover:` on the cell would not fix it either — that fires only while the pointer is
 * over that one cell, not anywhere on the row. `TableRow` marks itself `tw-group`.
 */
export const stickyCellGround = (
  sticky: TableColumnSticky | undefined,
  opts: { striped: boolean; hoverable: boolean; selected?: boolean },
): string => {
  if (!sticky) return '';

  /* § 299 — a SELECTED row's pinned cell repaints the selection ground and takes no
     hover, exactly as the row does: selected is brand, hover is neutral, never both. */
  if (opts.selected) return 'tw-bg-accent-tint';
  const base = opts.striped ? 'tw-bg-surface-sunken' : 'tw-bg-surface';
  /* § 256 — mirrors TableRow's computed hover exactly: a pinned cell must repaint the
     ground its row moves to, and a striped row moves UP to `surface`. */
  const hovered = opts.striped ? 'group-hover:tw-bg-surface' : 'group-hover:tw-bg-surface-hover-strong';
  return opts.hoverable ? `${base} ${hovered}` : base;
};

/**
 * The header ground for a pinned `<th>` — pinned to an EDGE (`TableColumn.sticky`) or
 * to the TOP (`stickyHeader`), since either one composites the cell over something
 * else and so needs it opaque. Mirrors `headerVariantStyles` in `TableHeader`: read
 * from the variant rather than hardcoded, so a `light` header does not sprout a `dark`
 * pinned column.
 */
export const stickyHeaderGround = (
  /** Is this header cell pinned at all — to an edge, to the top, or both? */
  isPinned: boolean,
  variant: TableHeaderVariant,
): string => {
  if (!isPinned) return '';
  return variant === 'light' ? 'tw-bg-surface-sunken' : 'tw-bg-tone-neutral-tint';
};
